import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, Building2, Store, Rocket, Zap } from "lucide-react";
import { PageHead } from "@/components/SEO";
import { MaskedLines, Reveal, Overline } from "@/components/Motion";
import { CircuitCanvas } from "@/components/CircuitCanvas";
import { Marquee } from "@/components/Marquee";
import { SERVICES } from "@/data/services";
import { fetchPosts } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1778319300667-a4a50dfc9dd5?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGJsdWUlMjBhYnN0cmFjdCUyMG5lb24lMjBsaW5lcyUyMGRhcmslMjB0ZWNofGVufDB8fHx8MTc5MDQ3NDU4Mnww&ixlib=rb-4.1.0&q=80&w=1600";
const ABOUT_IMG = "https://images.unsplash.com/photo-1760670399462-f5e479452c27?crop=entropy&cs=srgb&auto=format&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxkZXZlbG9wZXIlMjBjb2RpbmclMjBsYXB0b3AlMjBkYXJrJTIwc2NyZWVufGVufDB8fHx8MTc5MDQ3NDU4OXww&ixlib=rb-4.1.0&q=80&w=1200";

const MARQUEE_ITEMS = ["REACT", "NODE.JS", "PYTHON", "TYPESCRIPT", "FLUTTER", "POSTGRESQL", "AWS", "KUBERNETES", "OPENAI", "DOCKER", "GRAPHQL", "SWIFT", "KOTLIN", "FASTAPI"];

const STATS = [
  { value: "40+", label: "Products shipped" },
  { value: "99.99%", label: "Uptime engineering" },
  { value: "$50M+", label: "Client value created" },
  { value: "3×", label: "Faster release cycles" },
];

const PROCESS = [
  { n: "01", title: "Discover & Scope", desc: "We map your operation, constraints, and success metrics before a line of code. You get a written scope with real numbers — not a estimate shaped like a guess." },
  { n: "02", title: "Architect", desc: "System design, data models, and stack decisions documented and signed off together. No surprises buried in month three." },
  { n: "03", title: "Build in Sprints", desc: "Weekly demoable increments. You watch the product take shape and steer it — not a big reveal you hope you like." },
  { n: "04", title: "Launch & Scale", desc: "Zero-downtime deploys, observability wired in, and a handoff your team can actually own. We stay available; you stay in control." },
];

const AUDIENCES = [
  { icon: Building2, title: "Major Corporations", desc: "Modernization programs, internal platforms, and AI initiatives engineered to pass procurement, security review, and scale." },
  { icon: Store, title: "Growing SMBs", desc: "The custom systems that remove the spreadsheet chaos standing between you and your next stage of growth." },
  { icon: Rocket, title: "Founders & Mobile Apps", desc: "From napkin sketch to App Store — MVP in weeks, on architecture that survives product-market fit." },
];

const bentoSpan = ["lg:col-span-4", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2"];
const bentoWithImage = new Set([0, 4]);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const chipX = useSpring(mx, { stiffness: 60, damping: 18 });
  const chipY = useSpring(my, { stiffness: 60, damping: 18 });

  const [posts, setPosts] = useState([]);
  useEffect(() => {
    fetchPosts().then(setPosts).catch(() => setPosts([]));
  }, []);

  const onHeroMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - rect.left) / rect.width - 0.5) * 22);
    my.set(((e.clientY - rect.top) / rect.height - 0.5) * 22);
  };

  return (
    <div data-testid="home-page">
      <PageHead
        title="volt.software | Custom Software Engineering & SaaS Studio - Lincolnton, NC"
        description="volt.software builds high-performance custom software, SaaS products, mobile apps, custom AI agents, and legacy revamps for enterprises, SMBs, and startups. Based in Lincolnton, NC."
        path="/"
      />

      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={onHeroMouse}
        data-testid="hero-section"
        className="volt-grid-bg noise-bg relative flex min-h-screen items-center overflow-hidden pt-[72px]"
      >
        <CircuitCanvas className="absolute inset-0 h-full w-full opacity-75" />
        <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-blue-600/15 blur-[140px]" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-cyan-400/10 blur-[140px]" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 py-24 lg:grid-cols-[1.15fr_0.85fr] lg:px-10">
          <div>
            <Reveal y={16}>
              <Overline className="mb-6 flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 animate-pulse-glow rounded-full bg-cyan-400" />
                Volt.software — Lincolnton, NC
              </Overline>
            </Reveal>
            <MaskedLines
              data-testid="hero-headline"
              className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl"
              lines={[
                "Software",
                "engineered at",
                <span key="v" className="text-volt-gradient">full voltage.</span>,
              ]}
              delay={0.15}
            />
            <Reveal delay={0.55} className="mt-7 max-w-xl">
              <p data-testid="hero-subcopy" className="text-base leading-relaxed text-slate-400 sm:text-lg">
                From Fortune-scale platforms to a founder's first app — we design, build, and modernize
                custom software, SaaS products, mobile apps, and AI agents that outperform the off-the-shelf world.
              </p>
            </Reveal>
            <Reveal delay={0.7} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-600 px-8 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(56,189,248,0.5)]"
              >
                Initialize Your Build <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-8 py-4 font-display text-sm font-semibold text-cyan-300 transition-all duration-300 hover:border-cyan-400/70 hover:bg-cyan-400/10 hover:text-cyan-200"
              >
                Explore Services
              </Link>
            </Reveal>
            <Reveal delay={0.85} className="mt-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600">
                // Written proposals. One-business-day replies. You own the code.
              </p>
            </Reveal>
          </div>

          <motion.div style={{ y: imgY }} className="relative hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, clipPath: "inset(8% 8% 8% 8% round 24px)" }}
              animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 24px)" }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-3xl border border-cyan-400/20 shadow-[0_0_80px_rgba(31,92,255,0.25)]"
            >
              <img src={HERO_IMG} alt="Electric blue abstract light trails representing high-voltage software engineering" className="aspect-[4/5] w-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/90 via-transparent to-transparent" />
            </motion.div>

            <motion.div style={{ x: chipX, y: chipY }} className="absolute -left-8 top-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="glass-panel rounded-2xl px-5 py-4"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Deploy Frequency</p>
                <p className="mt-1 font-display text-2xl font-bold text-slate-50">Weekly<span className="text-cyan-400">.</span></p>
              </motion.div>
            </motion.div>
            <motion.div style={{ x: chipX, y: chipY }} className="absolute -right-6 bottom-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.15, duration: 0.8 }}
                className="glass-panel rounded-2xl px-5 py-4"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Migration Downtime</p>
                <p className="mt-1 font-display text-2xl font-bold text-slate-50">0 min<span className="text-cyan-400">.</span></p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-cyan-500/10 bg-[#FFFFFF]/60 backdrop-blur-md">
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-cyan-500/10 px-5 lg:grid-cols-4 lg:px-10">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={0.9 + i * 0.08} className="px-4 py-5 sm:px-6">
                <p className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* SERVICES BENTO */}
      <section data-testid="services-bento" className="relative mx-auto max-w-7xl px-5 py-28 lg:px-10">
        <Reveal>
          <Overline className="mb-4">// What we build</Overline>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Six disciplines.<br />One engineering standard.
            </h2>
            <Link to="/services" data-testid="bento-all-services" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 transition-colors hover:text-cyan-300">
              View all services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.06} className={`${bentoSpan[i]} sm:col-span-1`}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`bento-card-${s.slug}`}
                className="group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden rounded-2xl border border-cyan-500/10 bg-volt-surface/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-400/40 hover:shadow-[0_16px_60px_rgba(31,92,255,0.2)]"
              >
                {bentoWithImage.has(i) && (
                  <>
                    <img src={s.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-500 group-hover:scale-105 group-hover:opacity-40" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-volt-surface via-volt-surface/70 to-transparent" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5 text-cyan-400 transition-all duration-300 group-hover:border-cyan-400/60 group-hover:text-cyan-300">
                    <s.icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-slate-600 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cyan-300" />
                </div>
                <div className="relative mt-8">
                  <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.22em] text-cyan-500/80">{s.badge}</p>
                  <h3 className="font-display text-lg font-bold text-slate-50 transition-colors duration-300 group-hover:text-cyan-300">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" data-testid="process-section" className="relative border-t border-cyan-500/10 bg-[#EEF4FB] py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <Reveal>
              <Overline className="mb-4">// The Volt Method</Overline>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                High voltage.<br />Zero surprises.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                A four-stage delivery system refined across dozens of launches — engineered so you always know what's happening, what's next, and what it costs.
              </p>
              <Link
                to="/contact"
                data-testid="process-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-6 py-3 font-display text-sm font-semibold text-cyan-300 transition-all duration-300 hover:border-cyan-400/70 hover:bg-cyan-400/10"
              >
                Start Stage 01 <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="space-y-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`process-step-${p.n}`} className="group relative overflow-hidden rounded-2xl border border-cyan-500/10 bg-volt-surface/50 p-7 transition-all duration-300 hover:border-cyan-400/35 hover:bg-volt-surface/80">
                  <div className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-cyan-400 to-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="flex items-start gap-6">
                    <span className="stroke-text font-display text-5xl font-black leading-none sm:text-6xl">{p.n}</span>
                    <div>
                      <h3 className="font-display text-xl font-bold text-slate-50">{p.title}</h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / AUDIENCE */}
      <section id="about" data-testid="about-section" className="relative overflow-hidden py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl border border-cyan-400/15">
                <img src={ABOUT_IMG} alt="Volt engineer working in a dark studio lit by electric blue screens" className="aspect-[4/3] w-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#FFFFFF]/80 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 glass-panel rounded-xl px-4 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">HQ</p>
                  <p className="font-display text-sm font-semibold text-slate-100">Lincolnton, North Carolina</p>
                </div>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <Overline className="mb-4">// The studio</Overline>
                <h2 className="font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                  A senior engineering studio. Small enough to care. Sharp enough to ship anything.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-400">
                  volt.software is based in Lincolnton, NC, and builds for clients everywhere. No account managers relaying messages, no junior handoffs — the engineers you meet are the engineers who build. The same standard applies whether the client is a global enterprise or one person with an app idea.
                </p>
              </Reveal>
              <div className="mt-8 space-y-3">
                {AUDIENCES.map((a, i) => (
                  <Reveal key={a.title} delay={i * 0.08}>
                    <div data-testid={`audience-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-start gap-4 rounded-2xl border border-cyan-500/10 bg-volt-surface/50 p-5 transition-all duration-300 hover:border-cyan-400/35">
                      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5 text-cyan-400">
                        <a.icon className="h-4.5 w-4.5" strokeWidth={1.7} />
                      </span>
                      <div>
                        <h3 className="font-display text-base font-bold text-slate-100">{a.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-400">{a.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section data-testid="insights-preview" className="border-t border-cyan-500/10 bg-[#EEF4FB] py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Overline className="mb-4">// Volt Engineering Pulse</Overline>
                <h2 className="font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">Signals from the lab.</h2>
              </div>
              <Link to="/insights" data-testid="insights-all-link" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 transition-colors hover:text-cyan-300">
                All insights <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`home-post-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-cyan-500/10 bg-volt-surface/60 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-400/40"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-volt-surface to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-cyan-400/30 bg-[#FFFFFF]/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300 backdrop-blur-sm">{p.category}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-bold leading-snug text-slate-100 transition-colors duration-300 group-hover:text-cyan-300">{p.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-400">{p.excerpt}</p>
                    <p className="mt-auto pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">{p.read_time}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
            {posts.length === 0 &&
              [0, 1, 2].map((i) => (
                <div key={i} className="h-80 animate-pulse rounded-2xl border border-cyan-500/10 bg-volt-surface/40" />
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="relative overflow-hidden border-t border-cyan-500/10 py-28">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/15 blur-[140px]" />
        <div className="relative mx-auto max-w-4xl px-5 text-center">
          <Reveal>
            <Zap className="mx-auto mb-6 h-10 w-10 text-cyan-400" strokeWidth={1.5} />
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Ready to flip<br />the <span className="text-volt-gradient">switch?</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Tell us what you're building. We'll reply within one business day with a straight answer on approach, timeline, and cost.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-600 px-9 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_48px_rgba(56,189,248,0.55)]"
            >
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
